import { styled } from '@mui/material';
import moment from 'moment';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';
import { DRAFT_TYPES } from 'utils/constants';
import { getTooltipComponent } from 'utils/helper';

const DraftSubmissionCard = forwardRef(({ draftNum, feedback, onClickSubmit, onClickViewSubmission, ...rest }, ref) => {
    return (
        <RootStack ref={ref} {...rest}>
            <Stack spacing={2}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'}>Draft {draftNum}</Text>
                    {feedback ? (
                        <Stack alignItems={'flex-end'}>
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <CustomIcon kind={'checkmark'} />
                                <Text>Feedback Available</Text>
                            </Stack>
                            <Text color={'light'} variant={'p3'}>
                                {moment(feedback.created_on).format('MMM D [@] hh:mma')}
                            </Text>
                        </Stack>
                    ) : (
                        <Button startIcon={<Icon kind={'message'} />} variant={'secondary'} onClick={onClickSubmit}>
                            Get Feedback
                        </Button>
                    )}
                </Stack>
                {feedback && <Button onClick={onClickViewSubmission}>View Feedback</Button>}
            </Stack>
        </RootStack>
    );
});

const FinalDraftSubmissionCard = forwardRef(
    ({ assignment, submission, onClickSubmit, onClickViewSubmission, onClickResubmit, ...rest }, ref) => {
        const { t } = useTranslation('assignment');

        const tooltip = {
            title: t('tooltip-title', 'Due Date'),
            description: t('tooltip-description', 'The deadline to complete this deliverable'),
        };

        return (
            <RootStack ref={ref} spacing={3} {...rest}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'}>Final Submission</Text>
                    {submission ? (
                        <Stack alignItems={'flex-end'}>
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <CustomIcon kind={'checkmark'} />
                                <Text>Submitted</Text>
                            </Stack>
                            <Text color={'light'} variant={'p3'}>
                                {moment(submission.created_on).format('MMM D [@] hh:mma')}
                            </Text>
                        </Stack>
                    ) : (
                        <Button variant={'secondary'} onClick={onClickSubmit}>
                            Submit Assignment
                        </Button>
                    )}
                </Stack>
                {submission ? (
                    <Stack spacing={3}>
                        <Button onClick={onClickViewSubmission}>View Submission</Button>
                        <Button variant={'borderless'} onClick={onClickResubmit}>
                            Resubmit
                        </Button>
                    </Stack>
                ) : (
                    <Tag
                        tooltip={{
                            component: getTooltipComponent(tooltip.title, tooltip.description),
                            maxWidth: 128,
                        }}
                        type={'dueDate'}
                        value={assignment.end_on}
                    />
                )}
            </RootStack>
        );
    }
);

const SubmissionCard = forwardRef(({ variant, ...rest }, ref) => {
    const component = {
        [DRAFT_TYPES.DRAFT]: <DraftSubmissionCard ref={ref} {...rest} />,
        [DRAFT_TYPES.FINAL_DRAFT]: <FinalDraftSubmissionCard ref={ref} {...rest} />,
    }[variant];

    return component;
});

const RootStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '8px',
        width: '359px',
        padding: theme.spacing(4),
        background: theme.palette.ensightful.shades[0],
        boxShadow: theme.palette.ensightful.shadows[2],
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        color: theme.palette.ensightful.success[700],
    };
});

export default SubmissionCard;
